import React from 'react';
import { Folder, FolderCog, Check, Loader2, Circle, CircleDot, Settings, Trash2, Plus } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useWorkbookQuery } from '@/hooks/content-generator/use-workbook-query';
import { useAddToProject } from '@/hooks/private-research-generator/useAddToProject';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { motion } from 'framer-motion';

interface ResearchAddToProjectButtonProps {
  itemId: string;
  forceWhiteText?: boolean;
  dropdownLabel?: string;
  variant?: 'default' | 'auto' | 'compact';
  currentProjectId?: string | null; // Pass known project ID to avoid API calls
  isOwner?: boolean; // Pass ownership state to control UI behavior
  
  // Theme customization for compact variant
  gradientFrom?: string;
  gradientTo?: string;
  accentColor?: string;
  iconColor?: string;
  borderColor?: string;
  hoverBorderColor?: string;
  
  // Content customization for compact variant
  addText?: string;
  manageText?: string;
  addDescription?: string;
  manageDescription?: string;
}

const ResearchAddToProjectButton: React.FC<ResearchAddToProjectButtonProps> = ({ 
  itemId, 
  forceWhiteText, 
  dropdownLabel = "Add to Project",
  variant = 'auto',
  currentProjectId: propCurrentProjectId,
  isOwner,
  
  // Theme defaults - purple gradient for projects
  gradientFrom = 'from-purple-50/80 to-violet-50/80 dark:from-purple-950/30 dark:to-violet-950/30',
  gradientTo = 'hover:from-purple-100/80 hover:to-violet-100/80 dark:hover:from-purple-900/40 dark:hover:to-violet-900/40',
  accentColor = 'from-purple-400 to-violet-500',
  iconColor = 'text-purple-600 dark:text-purple-400',
  borderColor = 'border-purple-200/60 dark:border-purple-700/40',
  hoverBorderColor = 'hover:border-purple-300/70 dark:hover:border-purple-600/50',
  
  // Content defaults
  addText = 'Add to Project',
  manageText = 'Manage Project',
  addDescription = 'Assign to a project',
  manageDescription = 'Project assigned'
}) => {
  const location = useLocation();
  const { data: workbookData, isLoading: isLoadingProjects } = useWorkbookQuery({ 
    filterByStatus: 'all' // Get all non-archived projects
  });
  
  // Use the existing private research add to project hook
  const {
    currentProjectId: hookCurrentProjectId,
    updateResearchProject, // This updates workbook_id in workbook_research table
    removeFromProject,
    isPending,
    isOwner: hookIsOwner
  } = useAddToProject(itemId);
  
  // Use passed currentProjectId if available, otherwise fall back to hook
  const currentProjectId = propCurrentProjectId !== undefined ? propCurrentProjectId : hookCurrentProjectId;
  
  // Use passed isOwner if available, otherwise fall back to hook
  const effectiveIsOwner = isOwner !== undefined ? isOwner : hookIsOwner;
  
  const projects = workbookData?.projects || [];

  // Determine button styling based on context and state
  const getButtonStyling = () => {
    return {
      bgClass: "bg-white/20 dark:bg-black/20",
      borderClass: "border-white/20 dark:border-transparent",
      iconColor: forceWhiteText ? "text-white" : "text-gray-700 dark:text-white",
      shadow: "shadow-lg"
    };
  };

  const styling = getButtonStyling();

  const handleProjectSelect = async (projectId: string | null) => {
    if (isPending) return;
    await updateResearchProject(projectId); // This actually updates workbook_id in workbook_research
  };

  const handleRemoveFromProject = async () => {
    if (isPending) return;
    await removeFromProject();
  };
  
  const isAnythingLoading = isLoadingProjects;
  const isItemInAnyProject = currentProjectId !== null;

  // Render compact variant differently
  if (variant === 'compact') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <motion.button
            className={cn(
              "w-full relative overflow-hidden border-2 transition-all duration-300",
              `bg-gradient-to-br ${gradientFrom}`,
              borderColor,
              gradientTo,
              hoverBorderColor,
              "shadow-lg hover:shadow-xl",
              "group active:scale-[0.98]",
              "rounded-lg"
            )}
            whileHover={{ scale: 1.02, y: -1 }}
            whileTap={{ scale: 0.98 }}
            disabled={isAnythingLoading}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Subtle top accent gradient */}
            <div className={cn("absolute top-0 left-0 right-0 h-1 bg-gradient-to-r", accentColor)} />
            
            <div className="p-3">
              <div className="flex items-center gap-2">
                {/* Icon */}
                <div className={cn(
                  "flex-shrink-0 rounded-lg transition-colors duration-300",
                  "bg-white/60 dark:bg-white/10",
                  "group-hover:bg-white/80 dark:group-hover:bg-white/15",
                  "p-1.5"
                )}>
                  {isItemInAnyProject ? (
                    <FolderCog className={cn("h-4 w-4", iconColor)} />
                  ) : (
                    <Folder className={cn("h-4 w-4", iconColor)} />
                  )}
                </div>
                
                {/* Content */}
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2">
                    <h5 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {isItemInAnyProject ? manageText : addText}
                    </h5>
                    {isAnythingLoading && (
                      <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                    )}
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    {isItemInAnyProject ? manageDescription : addDescription}
                  </p>
                </div>
                
                {/* Status indicator */}
                <div className="flex-shrink-0">
                  {isItemInAnyProject ? (
                    <div className="w-2 h-2 rounded-full bg-green-500 dark:bg-green-400 shadow-sm" />
                  ) : (
                    <Plus className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>
            </div>
          </motion.button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="start" className="w-64" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel>{dropdownLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isAnythingLoading ? (
            <div className="flex items-center justify-center p-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : effectiveIsOwner === false ? (
            // Show informative message for non-owners
            <div className="px-2 py-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    Permission Required
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    Only the user who created this research can modify its project assignments.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Show project list for owners
            <>
              {workbookData?.projects?.map((project) => {
                const isCurrentProject = currentProjectId === project.id;
                const isDisabled = isPending;
                
                return (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => {
                      if (isDisabled) return;
                      
                      if (isCurrentProject) {
                        // Currently in this project, remove from it
                        removeFromProject?.();
                      } else {
                        // Not in this project, add to it
                        updateResearchProject(project.id);
                      }
                    }}
                    disabled={isDisabled}
                    className="flex items-center gap-3 p-3"
                  >
                    <div className="flex items-center justify-center w-4 h-4">
                      {isCurrentProject ? (
                        <CircleDot className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    <span className="flex-1 truncate text-sm">
                      {project.title}
                    </span>
                    {isCurrentProject && (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                  </DropdownMenuItem>
                );
              })}
              
              {/* Remove from project option */}
              {currentProjectId && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      if (!isPending) {
                        removeFromProject?.();
                      }
                    }}
                    disabled={isPending}
                    className="flex items-center gap-3 p-3 text-red-600 dark:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="text-sm">Remove from Project</span>
                  </DropdownMenuItem>
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default icon variant (for card buttons)
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <motion.button
            className={cn(
              "inline-flex items-center justify-center p-1 rounded-full",
              styling.bgClass,
              "border",
              styling.borderClass,
              styling.shadow,
              "backdrop-blur-lg",
              "hover:scale-105 active:scale-95 transition-transform duration-200"
            )}
            onClick={(e) => e.stopPropagation()}
            disabled={isPending}
          >
            {isItemInAnyProject ? (
              <FolderCog size={20} className={styling.iconColor} />
            ) : (
              <Folder size={20} className={styling.iconColor} />
            )}
          </motion.button>
        </DropdownMenuTrigger>

        <DropdownMenuContent className="w-80" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel>{dropdownLabel}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {isAnythingLoading ? (
            <div className="flex items-center justify-center p-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          ) : effectiveIsOwner === false ? (
            // Show informative message for non-owners
            <div className="px-2 py-3">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Settings className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    Permission Required
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                    Only the user who created this research can modify its project assignments.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            // Show project list for owners
            <>
              {workbookData?.projects?.map((project) => {
                const isCurrentProject = currentProjectId === project.id;
                const isDisabled = isPending;
                
                return (
                  <DropdownMenuItem
                    key={project.id}
                    onClick={() => {
                      if (isDisabled) return;
                      
                      if (isCurrentProject) {
                        // Currently in this project, remove from it
                        removeFromProject?.();
                      } else {
                        // Not in this project, add to it
                        updateResearchProject(project.id);
                      }
                    }}
                    disabled={isDisabled}
                    className="flex items-center gap-3 p-3"
                  >
                    <div className="flex items-center justify-center w-4 h-4">
                      {isCurrentProject ? (
                        <CircleDot className="h-4 w-4 text-blue-600" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                    <span className="flex-1 truncate text-sm">
                      {project.title}
                    </span>
                    {isCurrentProject && (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                  </DropdownMenuItem>
                );
              })}
              
              {/* Remove from project option */}
              {currentProjectId && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      if (!isPending) {
                        removeFromProject?.();
                      }
                    }}
                    disabled={isPending}
                    className="flex items-center gap-3 p-3 text-red-600 dark:text-red-400"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="text-sm">Remove from Project</span>
                  </DropdownMenuItem>
                </>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};

export default ResearchAddToProjectButton; 